/**
 * StoreSwitcher — compact dropdown for switching between stores in the
 * top header. Reactive to WatermelonDB so newly-synced stores appear
 * automatically. Switching writes to useAppStore.setActiveStore which
 * triggers a re-sync via AppShell's effect.
 */

import { useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Check, ChevronDown, Building2 } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { database } from '@/db';
import Store from '@/db/models/Store';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { cn } from '@/utils/cn';

export function StoreSwitcher() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);
  const setActiveStore = useAppStore((s) => s.setActiveStore);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const stores = useObservable<Store[]>(
    () => {
      if (!merchantId) return null;
      return database.get<Store>('stores')
        .query(
          Q.where('merchant_id', merchantId),
          Q.where('is_deleted', false),
          Q.where('is_active', true),
        )
        .observe();
    },
    [merchantId],
    [],
  );

  const active = stores.find((s) => s.id === activeStoreId);

  async function handlePick(id: string) {
    if (id === activeStoreId) { setOpen(false); return; }
    setBusy(true);
    try {
      await setActiveStore(id);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  // When the local WatermelonDB has no Store rows yet (fresh install or
  // pre-sync state) show a hint instead of silently rendering nothing —
  // otherwise the header looks like the store name is just missing.
  if (stores.length === 0) {
    return (
      <div className="inline-flex items-center gap-2 text-body text-fg-tertiary">
        <Building2 size={14} />
        <span>{activeStoreId ? 'Store data not synced — click Sync now' : 'No store'}</span>
      </div>
    );
  }

  // Hide entirely when only one store — no need for a switcher.
  if (stores.length === 1) {
    return (
      <div className="inline-flex items-center gap-2 text-body text-fg-secondary">
        <Building2 size={14} />
        <span>{stores[0].name}</span>
      </div>
    );
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-body text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary transition-colors duration-120 ease-apple disabled:opacity-50"
        >
          <Building2 size={14} />
          <span className="max-w-[110px] truncate sm:max-w-[200px]">{active?.name ?? 'Pick a store'}</span>
          <ChevronDown size={14} className="text-fg-tertiary" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="z-50 min-w-[220px] overflow-hidden rounded-md border border-stroke-hairline/15 bg-bg-surface p-1 shadow-lg"
        >
          <div className="px-2 py-1 text-label uppercase text-fg-tertiary">Switch store</div>
          {stores.map((s) => {
            const isActive = s.id === activeStoreId;
            return (
              <DropdownMenu.Item
                key={s.id}
                onSelect={(e) => { e.preventDefault(); void handlePick(s.id); }}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-body outline-none',
                  isActive ? 'text-accent-ink' : 'text-fg-primary hover:bg-bg-subtle',
                )}
              >
                <Check size={14} className={cn(isActive ? 'opacity-100' : 'opacity-0')} />
                <span className="flex-1 truncate">{s.name}</span>
                {s.city && <span className="text-label text-fg-tertiary">{s.city}</span>}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
